import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Button,
    Col,
    Container,
    Form,
    Pagination,
    Placeholder,
    Row,
    Table,
} from 'react-bootstrap';
import { BsPencil, BsTrash3 } from 'react-icons/bs';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import MainContainer from '../../../MainContainer';
import QuickReplyModal from '../../../QuickReplyModal';
import TicketsSearch from '../../tickets/TicketsSearch';
import EmbedListQuickFilters from '../../embedForms/embedList/EmbedListQuickFilters';
import EmbedListLayout from '../../embedForms/embedList/EmbedListLayout';
import EmbedListFilter from '../../embedForms/embedList/EmbedListFilter';
import EmbedListSortableTh from '../../embedForms/embedList/EmbedListSortableTh';
import { EmbedFormsActiveSelect } from '../../embedForms/embedFormsActiveSelect';
import { fetchUsers } from '../../../../redux/actions';
import { baseURL } from '../../../../helpers/baseURL';
import { headers } from '../../../../helpers/Requests';
import { showDeleteConfirm } from '../../../ItemDelete';
import successAlert from '../../../../helpers/successAlert';
import failAlert from '../../../../helpers/failAlert';
import { stripHtmlPreview } from '../../../../helpers/quickReplyVariables';
import tableStyles from '../../tickets/TicketsTable.module.css';
import plus from '../../../../../css/img/plus.svg';
import '../../embedForms/embed-form-editor.css';
import {
    buildCreatorQuickFilterOptions,
    buildQuickRepliesApiParams,
    QUICK_REPLIES_ADVANCED_FILTER_OPTIONS,
} from './quickRepliesListUtils';

const DEFAULT_FILTERS = {
    text: '',
    created_by: [],
    created_at: '',
    created_at_range: [],
};

const DEFAULT_COLUMNS = {
    name: { visible: true, label: 'Name' },
    preview: { visible: true, label: 'Preview' },
    is_active: { visible: true, label: 'Status' },
    created_by: { visible: true, label: 'Created by' },
    created_at: { visible: true, label: 'Created' },
};

const QuickReplies = () => {
    const dispatch = useDispatch();
    const users = useSelector((state) => state.usersReducer?.users) ?? [];
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [pagination, setPagination] = useState({
        total: 0,
        per_page: 10,
        current_page: 1,
        last_page: 1,
    });
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [sort, setSort] = useState({ by: 'created_at', direction: 'desc' });
    const [columns, setColumns] = useState(() => {
        try {
            const saved = localStorage.getItem('quickRepliesTableColumns');
            return saved ? JSON.parse(saved) : DEFAULT_COLUMNS;
        } catch {
            return DEFAULT_COLUMNS;
        }
    });
    const [showModal, setShowModal] = useState(false);
    const [selectedReply, setSelectedReply] = useState(null);
    const [updatingActiveId, setUpdatingActiveId] = useState(null);

    useEffect(() => {
        dispatch(fetchUsers());
    }, [dispatch]);

    const load = useCallback(async (pageNum = page, pageSize = perPage, filtersSnapshot = filters, sortSnapshot = sort) => {
        setLoading(true);
        try {
            const params = buildQuickRepliesApiParams(filtersSnapshot, sortSnapshot, {
                page: pageNum,
                perPage: pageSize,
            });
            const { data } = await axios.get(`${baseURL}quick-replies`, { ...headers, params });
            if (Array.isArray(data)) {
                setRows(data);
                setPagination({
                    total: data.length,
                    per_page: data.length,
                    current_page: 1,
                    last_page: 1,
                });
            } else {
                setRows(Array.isArray(data?.data) ? data.data : []);
                setPagination({
                    total: data?.total ?? 0,
                    per_page: data?.per_page ?? pageSize,
                    current_page: data?.current_page ?? pageNum,
                    last_page: data?.last_page ?? 1,
                });
            }
        } catch (error) {
            failAlert(error?.response?.data?.message ?? 'Failed to load quick replies.');
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [filters, sort, page, perPage]);

    useEffect(() => {
        load(page, perPage, filters, sort);
    }, [page, perPage, filters, sort]);

    const creatorOptions = useMemo(() => buildCreatorQuickFilterOptions(users), [users]);

    const quickFilters = useMemo(
        () => [{ key: 'created_by', label: 'Created by', options: creatorOptions }],
        [creatorOptions]
    );

    const handleSearchDebounced = useCallback((text) => {
        setPage(1);
        setFilters((prev) => ({ ...prev, text: (text ?? '').trim() }));
    }, []);

    const handleFilterChange = (newFilters) => {
        setPage(1);
        setFilters(newFilters);
    };

    const handleSortChange = (column) => {
        setPage(1);
        if (column === 'is_active') {
            setSort({
                by: column,
                direction: sort.by === column && sort.direction === 'desc' ? 'asc' : 'desc',
            });
            return;
        }
        setSort({
            by: column,
            direction: sort.by === column && sort.direction === 'asc' ? 'desc' : 'asc',
        });
    };

    const handleColumnVisibilityChange = (column) => {
        const updated = {
            ...columns,
            [column]: { ...columns[column], visible: !columns[column].visible },
        };
        setColumns(updated);
        localStorage.setItem('quickRepliesTableColumns', JSON.stringify(updated));
    };

    const visibleColumnCount = Object.values(columns).filter((c) => c.visible).length + 1;

    const totalPages = Math.max(1, Math.ceil(pagination.total / perPage));
    const currentPage = pagination.current_page || page;

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 16;
        let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let end = start + maxVisible - 1;
        if (end > totalPages) {
            end = totalPages;
            start = Math.max(1, end - maxVisible + 1);
        }
        if (start > 1) {
            pages.push(
                <Pagination.Item key={1} onClick={() => setPage(1)}>
                    1
                </Pagination.Item>
            );
            if (start > 2) {
                pages.push(<Pagination.Ellipsis key="start-ellipsis" />);
            }
        }
        for (let i = start; i <= end; i++) {
            pages.push(
                <Pagination.Item
                    key={i}
                    active={i === currentPage}
                    onClick={() => setPage(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }
        if (end < totalPages) {
            if (end < totalPages - 1) {
                pages.push(<Pagination.Ellipsis key="end-ellipsis" />);
            }
            pages.push(
                <Pagination.Item key={totalPages} onClick={() => setPage(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }
        return pages;
    };

    const handlePaginationChange = (nextPage, nextPerPage) => {
        if (nextPerPage !== perPage) {
            setPerPage(nextPerPage);
            setPage(1);
            return;
        }
        setPage(nextPage);
    };

    const openCreateModal = () => {
        setSelectedReply(null);
        setShowModal(true);
    };

    const openEditModal = (reply) => {
        setSelectedReply(reply);
        setShowModal(true);
    };

    const handleSaved = () => {
        load(page, perPage, filters, sort);
    };

    const handleDelete = (reply) => {
        showDeleteConfirm(reply, 'Quick Reply', 'quick-reply', () => load(page, perPage, filters, sort), dispatch);
    };

    const handleActiveChange = async (reply, isActive) => {
        setUpdatingActiveId(reply.id);
        try {
            const { data } = await axios.patch(
                `${baseURL}quick-reply-status/${reply.id}`,
                { is_active: isActive },
                headers
            );
            setRows((prev) => prev.map((row) => (
                row.id === reply.id ? { ...row, is_active: isActive } : row
            )));
            successAlert(data?.message ?? 'Status updated.');
        } catch (error) {
            failAlert(error?.response?.data?.message ?? 'Failed to update status.');
        } finally {
            setUpdatingActiveId(null);
        }
    };

    const renderLoadingSkeleton = () => {
        const skeletonRows = 8;
        return (
            <div className={`${tableStyles.tableWrapper} embed-ticket-forms-table-wrap`}>
                <Table className={tableStyles.table}>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Preview</th>
                            <th>Status</th>
                            <th>Created by</th>
                            <th>Created</th>
                            <th className={tableStyles.stickyActions} style={{ width: 110, textAlign: 'center' }}>
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {Array.from({ length: skeletonRows }).map((_, idx) => (
                            <tr key={idx}>
                                <td>
                                    <Placeholder as="div" animation="glow">
                                        <Placeholder xs={7} style={{ display: 'block', borderRadius: 4 }} />
                                    </Placeholder>
                                </td>
                                <td>
                                    <Placeholder as="div" animation="glow">
                                        <Placeholder xs={10} style={{ display: 'block', borderRadius: 4 }} />
                                    </Placeholder>
                                </td>
                                <td className={tableStyles.selectCell}>
                                    <Placeholder as="div" animation="glow">
                                        <Placeholder xs={8} style={{ display: 'block', borderRadius: 4 }} />
                                    </Placeholder>
                                </td>
                                <td>
                                    <Placeholder as="div" animation="glow">
                                        <Placeholder xs={6} style={{ display: 'block', borderRadius: 4 }} />
                                    </Placeholder>
                                </td>
                                <td className="text-muted">
                                    <Placeholder as="div" animation="glow">
                                        <Placeholder xs={5} style={{ display: 'block', borderRadius: 4 }} />
                                    </Placeholder>
                                </td>
                                <td className={tableStyles.stickyActions} style={{ textAlign: 'center' }}>
                                    <div style={{ display: 'inline-flex', gap: 5 }}>
                                        <Placeholder.Button
                                            variant="secondary"
                                            style={{ width: 26, height: 26, padding: 0, borderRadius: 5 }}
                                            aria-hidden="true"
                                        />
                                        <Placeholder.Button
                                            variant="secondary"
                                            style={{ width: 26, height: 26, padding: 0, borderRadius: 5 }}
                                            aria-hidden="true"
                                        />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        );
    };

    return (
        <MainContainer>
            <Container fluid className="embed-forms-page">
                <Row className="pt-4 pb-2">
                    <Col className="table-page-header" style={{ justifyContent: 'flex-start' }}>
                        <h5 className="flex-grow-1 m-0 text-align-left">Quick replies</h5>
                    </Col>
                </Row>

                <Row className="pb-3">
                    <Col className="d-flex flex-wrap align-items-start gap-2">
                        <TicketsSearch
                            value={filters.text}
                            onDebouncedChange={handleSearchDebounced}
                            isLoading={loading}
                            placeholder="Search quick replies"
                        />
                        <EmbedListQuickFilters
                            quickFilters={quickFilters}
                            filters={filters}
                            onFilterChange={handleFilterChange}
                            isLoading={loading}
                            showPerFilterClear={false}
                        />
                        <div className="d-flex flex-wrap align-items-start gap-2 tickets-toolbar-actions">
                            <EmbedListLayout
                                columns={columns}
                                onColumnVisibilityChange={handleColumnVisibilityChange}
                            />
                            <EmbedListFilter
                                filterOptions={QUICK_REPLIES_ADVANCED_FILTER_OPTIONS}
                                isLoading={loading}
                                filters={filters}
                                onFilterChange={handleFilterChange}
                            />
                            <Button
                                type="button"
                                variant="primary"
                                onClick={openCreateModal}
                                style={{ whiteSpace: 'nowrap' }}
                                disabled={loading}
                            >
                                <img src={plus} alt="" />
                                <span className="ms-1">New quick reply</span>
                            </Button>
                        </div>
                    </Col>
                </Row>

                <Row>
                    <Col className="overflow-x-auto">
                        {loading ? (
                            renderLoadingSkeleton()
                        ) : (
                            <>
                            <div className={`${tableStyles.tableWrapper} embed-ticket-forms-table-wrap`}>
                                <Table className={tableStyles.table} hover>
                                    <thead>
                                        <tr>
                                            {columns.name.visible && (
                                                <EmbedListSortableTh
                                                    columnKey="name"
                                                    label={columns.name.label}
                                                    sort={sort}
                                                    onSortChange={handleSortChange}
                                                />
                                            )}
                                            {columns.preview.visible && (
                                                <th>{columns.preview.label}</th>
                                            )}
                                            {columns.is_active.visible && (
                                                <EmbedListSortableTh
                                                    columnKey="is_active"
                                                    label={columns.is_active.label}
                                                    sort={sort}
                                                    onSortChange={handleSortChange}
                                                />
                                            )}
                                            {columns.created_by.visible && (
                                                <th>{columns.created_by.label}</th>
                                            )}
                                            {columns.created_at.visible && (
                                                <EmbedListSortableTh
                                                    columnKey="created_at"
                                                    label={columns.created_at.label}
                                                    sort={sort}
                                                    onSortChange={handleSortChange}
                                                />
                                            )}
                                            <th
                                                className={tableStyles.stickyActions}
                                                style={{ width: 110, textAlign: 'center' }}
                                            >
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.length === 0 ? (
                                            <tr>
                                                <td
                                                    colSpan={visibleColumnCount}
                                                    className="text-muted embed-ticket-forms-table__empty"
                                                >
                                                    No quick replies yet.
                                                </td>
                                            </tr>
                                        ) : (
                                            rows.map((reply) => (
                                                <tr
                                                    key={reply.id}
                                                    className="embed-ticket-forms-table-row"
                                                    onClick={() => openEditModal(reply)}
                                                >
                                                    {columns.name.visible && (
                                                        <td className="embed-ticket-forms-name-cell">
                                                            <span className={tableStyles.title}>{reply.name}</span>
                                                        </td>
                                                    )}
                                                    {columns.preview.visible && (
                                                        <td className="text-muted small">
                                                            {reply.preview || stripHtmlPreview(reply.content) || '—'}
                                                        </td>
                                                    )}
                                                    {columns.is_active.visible && (
                                                        <td
                                                            className={tableStyles.selectCell}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <EmbedFormsActiveSelect
                                                                isActive={reply.is_active}
                                                                disabled={updatingActiveId === reply.id}
                                                                onChange={(value) => handleActiveChange(reply, value)}
                                                            />
                                                        </td>
                                                    )}
                                                    {columns.created_by.visible && (
                                                        <td>{reply.created_by?.name ?? '—'}</td>
                                                    )}
                                                    {columns.created_at.visible && (
                                                        <td className="text-muted">{reply.created_at || '—'}</td>
                                                    )}
                                                    <td
                                                        className={tableStyles.stickyActions}
                                                        style={{ textAlign: 'center' }}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <div style={{ display: 'inline-flex', gap: 5 }}>
                                                            <button
                                                                type="button"
                                                                className={tableStyles.actionBtn}
                                                                title="Edit"
                                                                aria-label="Edit"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openEditModal(reply);
                                                                }}
                                                            >
                                                                <BsPencil size={11} aria-hidden />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className={tableStyles.actionBtn}
                                                                title="Delete"
                                                                aria-label="Delete"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDelete(reply);
                                                                }}
                                                            >
                                                                <BsTrash3 size={11} aria-hidden />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                            <div className={tableStyles.tableFooter}>
                                <div className="d-flex align-items-center flex-wrap" style={{ gap: 10 }}>
                                    <Pagination className={tableStyles.pagination}>
                                        <Pagination.Prev
                                            disabled={currentPage <= 1}
                                            onClick={() => handlePaginationChange(currentPage - 1, perPage)}
                                        />
                                        {getPageNumbers()}
                                        <Pagination.Next
                                            disabled={currentPage >= totalPages}
                                            onClick={() => handlePaginationChange(currentPage + 1, perPage)}
                                        />
                                    </Pagination>
                                    <label htmlFor="quickRepliesPerPageSelect" className={tableStyles.perPageLabel}>
                                        Page Size:
                                    </label>
                                    <Form.Control
                                        as="select"
                                        id="quickRepliesPerPageSelect"
                                        value={perPage}
                                        onChange={(e) => handlePaginationChange(1, parseInt(e.target.value, 10))}
                                        className={tableStyles.perPage}
                                        style={{ width: 80 }}
                                    >
                                        {[10, 20, 50, 100].map((size) => (
                                            <option key={size} value={size}>
                                                {size}
                                            </option>
                                        ))}
                                    </Form.Control>
                                    <span className={tableStyles.results}>
                                        Showing {rows.length} of {pagination.total} quick replies
                                    </span>
                                </div>
                            </div>
                            </>
                        )}
                    </Col>
                </Row>
            </Container>

            <QuickReplyModal
                show={showModal}
                onHide={() => setShowModal(false)}
                quickReply={selectedReply}
                onSaved={handleSaved}
            />
        </MainContainer>
    );
};

export default QuickReplies;
